import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

const AccountMappingModal = ({ 
  open, 
  onClose, 
  selectedSource, 
  sourceAccounts, 
  companyId, 
  csrfToken 
}) => {
  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [mappings, setMappings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Load Xero accounts when modal opens
  useEffect(() => {
    if (open && companyId) {
      loadXeroAccounts();
    }
  }, [open, companyId]);

  // Initialize mappings when source accounts change
  useEffect(() => {
    if (sourceAccounts && sourceAccounts.length > 0) {
      const initialMappings = {};
      sourceAccounts.forEach(account => {
        initialMappings[account.id] = account.dependency_id || '';
      });
      setMappings(initialMappings);
    }
  }, [sourceAccounts]);

  const loadXeroAccounts = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/internal/companies/${companyId}/accounts`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setXeroAccounts(data);
    } catch (err) {
      console.error('Failed to load Xero accounts:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMappingChange = (accountId, xeroAccountId) => {
    setMappings(prev => ({
      ...prev,
      [accountId]: xeroAccountId
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);
    
    try {
      const dependencies = Object.entries(mappings).map(([accountId, dependencyId]) => ({
        account_id: parseInt(accountId),
        dependency_id: dependencyId ? parseInt(dependencyId) : null
      })).filter(dep => dep.dependency_id !== null);
      
      const response = await fetch(`/api/internal/companies/${companyId}/sources/${selectedSource.id}/account_dependencies`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ dependencies })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Refresh the page to show updated mappings
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Failed to save account dependencies:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const getTokenName = (accountName) => {
    const parts = accountName.split(' - ');
    return parts[parts.length - 1] || accountName;
  };

  const getTokenIcon = (tokenName) => {
    return `/tokens/${tokenName.toUpperCase()}.png`;
  };

  if (!selectedSource || !sourceAccounts) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Map {selectedSource.display_name} Accounts to Xero
          </Typography>
          <Button onClick={onClose} size="small">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Account mappings saved successfully!
          </Alert>
        )}
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {selectedSource.display_name} - Account Mapping
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280' }}>
                Account name
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280' }}>
                Choose Xero account
              </Typography>
            </Box>
            
            {sourceAccounts.map((account) => {
              const tokenName = getTokenName(account.name);
              
              return (
                <Box key={account.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      component="img"
                      src={getTokenIcon(tokenName)}
                      alt={`${tokenName} icon`}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        mr: 1
                      }}
                      onError={(e) => {
                        e.target.src = '/tokens/default.png';
                        e.target.onerror = null;
                      }}
                    />
                    <Typography variant="body2">
                      {account.name}
                    </Typography>
                  </Box>
                  
                  <Autocomplete
                    options={[{ id: '', code: '', name: 'No mapping' }, ...xeroAccounts]}
                    getOptionLabel={(option) => {
                      if (option.id === '') return 'No mapping';
                      return `${option.code} - ${option.name}`;
                    }}
                    value={xeroAccounts.find(acc => acc.id === mappings[account.id]) || { id: '', code: '', name: 'No mapping' }}
                    onChange={(event, newValue) => {
                      handleMappingChange(account.id, newValue?.id || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Xero account"
                        size="small"
                        placeholder="Search Xero accounts..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option.id === '' ? (
                          <Typography sx={{ fontStyle: 'italic', color: '#6b7280' }}>
                            No mapping
                          </Typography>
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.code} - {option.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              Type: {option.type}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter(option => 
                        option.id === '' || 
                        option.code?.toLowerCase().includes(searchTerm) ||
                        option.name?.toLowerCase().includes(searchTerm) ||
                        option.type?.toLowerCase().includes(searchTerm)
                      );
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          disabled={isSaving || isLoading}
        >
          {isSaving ? 'Saving...' : 'Save Mappings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AccountMappingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedSource: PropTypes.shape({
    id: PropTypes.number.isRequired,
    display_name: PropTypes.string.isRequired
  }),
  sourceAccounts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    dependency_id: PropTypes.number
  })),
  companyId: PropTypes.number,
  csrfToken: PropTypes.string.isRequired
};

export default AccountMappingModal; 
